import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IAssistantChange, IAssistantTextChange } from '../assistant.models';

interface IListItem{
	text: string;
	/** pairs: descrizione sotto il titolo */
	detail?: string;
	state: 'added' | 'removed' | 'kept';
}

interface ITextView{
	field: string;
	label: string;
	format: IAssistantTextChange['format'];
	before: string | null;
	after: string | null;
	items: IListItem[];
	/** descrizione completa lunga: chiusa con sfumatura finché non si apre */
	long: boolean;
}

/**
 * Testi di vendita di una lingua dentro il cartellino della proposta. Il dopo si legge come apparirà (la descrizione
 * completa resa in HTML, già ripulito dal server e di nuovo da Angular), il prima resta a portata ma in secondo piano.
 */
@Component({
	selector: 'assistant-text-change',
	templateUrl: './text-change.component.html',
	styleUrls: ['./text-change.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AssistantTextChangeComponent{

	/** oltre questa lunghezza (testo senza tag) la descrizione completa parte chiusa */
	static readonly LONG_TEXT = 360;

	@Input() set change(change: IAssistantChange){
		this.isNew = change.op === 'create';
		this.visibility = AssistantTextChangeComponent.visibility(change);
		this.visible = !!change.active?.after;
		this.texts = (change.texts || []).map(text => AssistantTextChangeComponent.view(text));
	}

	isNew = false;
	visibility: string | null = null;
	visible = false;
	texts: ITextView[] = [];
	/** campi html aperti (testo intero) e campi con il testo attuale mostrato */
	opened = new Set<string>();
	showingBefore = new Set<string>();

	toggle(set: Set<string>, field: string): void{
		set.has(field) ? set.delete(field) : set.add(field);
	}

	private static visibility(change: IAssistantChange): string | null{
		if(!change.active) return null;
		if(change.active.before === null) return change.active.after ? 'Visibile subito su sito e widget' : 'Salvata ma nascosta su sito e widget';
		return change.active.after ? 'Diventa visibile su sito e widget' : 'Viene nascosta su sito e widget';
	}

	static view(text: IAssistantTextChange): ITextView{
		const asList = (value: IAssistantTextChange['before']) => Array.isArray(value) ? value.map(item => String(item)) : [];
		const asPairs = (value: IAssistantTextChange['before']) => Array.isArray(value) ? value.map(item => Array.isArray(item) ? [String(item[0] ?? ''), String(item[1] ?? '')] : [String(item), '']) : [];
		const asText = (value: IAssistantTextChange['before']) => {
			if(value === null || value === undefined || value === '') return null;
			return Array.isArray(value) ? value.join(', ') : String(value);
		};
		const isList = text.format === 'list' || text.format === 'pairs';
		const view: ITextView = {
			field: text.field,
			label: text.label,
			format: text.format,
			before: isList ? null : asText(text.before),
			after: isList ? null : asText(text.after),
			items: [],
			long: false
		};
		if(text.format === 'list'){
			const before = asList(text.before);
			const after = asList(text.after);
			view.items = [
				...after.map(item => ({ text: item, state: before.includes(item) ? 'kept' : 'added' } as IListItem)),
				...before.filter(item => !after.includes(item)).map(item => ({ text: item, state: 'removed' } as IListItem))
			];
		}
		if(text.format === 'pairs'){
			const key = (pair: string[]) => pair[0] + '\u0000' + pair[1];
			const before = asPairs(text.before);
			const after = asPairs(text.after);
			const beforeKeys = before.map(key);
			const afterKeys = after.map(key);
			view.items = [
				...after.map(pair => ({ text: pair[0], detail: pair[1], state: beforeKeys.includes(key(pair)) ? 'kept' : 'added' } as IListItem)),
				...before.filter(pair => !afterKeys.includes(key(pair))).map(pair => ({ text: pair[0], detail: pair[1], state: 'removed' } as IListItem))
			];
		}
		if(text.format === 'html'){
			const plain = (view.after || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
			view.long = plain.length > AssistantTextChangeComponent.LONG_TEXT;
		}
		return view;
	}
}
